// ─────────────────────────────────────────────────────────────────────────────
// The two money screens' server-written vocabulary, in the reading language.
//
// The credit ledger and the accounting line list both display strings that a
// SERVICE wrote, not the client:
//
//   • `metadata.action` on a wallet transaction («رونویسی صوتی», «خلاصه ویزیت»,
//     «بررسی تداخل دارویی») — written by the AI service's `_deduct_credit`.
//   • `description` on an accounting invoice line («ویزیت») — written by the
//     accounting service when it opens a visit invoice from a contract.
//
// Both are CLOSED vocabularies, so like the drug formulary they are translated
// at the DISPLAY EDGE: value → key → `t`. Nothing here changes what is stored
// or sent; the ledger keeps the Persian it was written with, and a value this
// file has not learned about renders AS ITSELF rather than as a blank or a
// humanised key. That fallback is the whole reason this is safe to ship ahead
// of the services: a new action string shows the doctor something true.
//
// The tables are keyed on the exact strings the services write, and are re-keyed
// through `normalizeFormularyValue` at module load, so a ZWNJ or an Arabic
// yeh/kaf in either the table or the stored value cannot break the match.
// ─────────────────────────────────────────────────────────────────────────────
import { vocabularyLookup } from '@parsina/framework/clinical/src/lib/drugVocabulary';

// Wallet transaction `metadata.action`. Every literal below is grepped from
// `action=` in the AI service's app/endpoints/grpc_server.py — that is the only
// writer. Keep them in sync when a new charged RPC lands.
//
// i18n-exempt: MATCHERS against values the AI service stores on the ledger, not
// UI copy. The Persian on the left is what the wallet row holds; the
// translation is the key on the right.
const CREDIT_ACTION_KEYS = {
  "رونویسی صوتی": 'transcription',
  "خلاصه ویزیت": 'visitSummary',
  "تکمیل یادداشت SOAP": 'soapNoteCompletion',
  "تکمیل SOAP": 'soapRefine',
  "سوال از دستیار": 'askAssistant',
  "استخراج یافته‌های دندان": 'dentalFindings',
  "بررسی تداخل دارویی": 'interactionCheck',
  "بررسی نسخه": 'prescriptionReview',
  "پیشنهاد آزمایش و دارو": 'testAndDrugSuggestions',
  "پردازش OCR": 'ocrProcessing',
  "OCR سند": 'documentOcr',
  "تشخیص افتراقی": 'differentialDiagnosis',
  "هشدار اورژانس": 'emergencyAlert',
  "نامه ارجاع": 'referralLetter',
  "دستورالعمل ترخیص": 'dischargeInstructions',
  "پیگیری بعد از ویزیت": 'postVisitFollowUp',
  "دستورات پزشک": 'doctorOrders',
  "برنامه درمانی هوشمند": 'treatmentPlan',
  "سوالات تأیید تشخیص": 'confirmationQuestions',
};

/** «رونویسی صوتی» → «Voice transcription», or the value itself. */
export const creditActionLabel = vocabularyLookup(CREDIT_ACTION_KEYS, 'credits.action');

// Accounting invoice-line `description`. Only the visit line is a fixed string;
// every other line's description is the clinic's own `order_catalog` service
// name, which is that clinic's data and renders as itself.
//
// i18n-exempt: matchers against stored accounting values, as above.
const LINE_DESCRIPTION_KEYS = {
  "ویزیت": 'visit',
};

const lineDescriptionLookup = vocabularyLookup(LINE_DESCRIPTION_KEYS, 'accounting.lineDescription');

/**
 * «ویزیت» → «Visit», or the value itself.
 *
 * The accounting service writes the visit line two ways: bare «ویزیت» when the
 * invoice is opened from a contract, and «ویزیت — {visit_type}» when it is
 * opened from a clinic event. The head is the closed vocabulary; the tail is the
 * appointment's own `visit_type` (`"first"`, and whatever the clinic sends), so
 * it is left exactly as stored.
 */
export function accountingLineLabel(description, t) {
  const raw = String(description || '').trim();
  if (!raw) return '';
  const direct = lineDescriptionLookup(raw, t);
  if (direct !== raw) return direct;

  const parts = raw.match(/^(.+?)\s*—\s*(.+)$/);
  if (parts) {
    const head = lineDescriptionLookup(parts[1], t);
    if (head !== parts[1].trim()) return `${head} — ${parts[2]}`;
  }
  return raw;
}
